import React, { useState, useEffect, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Card, CardHeader, CardContent, Radio, RadioGroup, FormControlLabel, Fade } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Loader, Button, colors, Typography, TextInput, Alert } from 'kangarootime-ui';
import moment from 'moment';
import DatePickerInput from './DatePickerInput';
import ktRequest from '../../utils/request';
import TotalsForDay from './TotalsForDay';
import Select from '../../components/Select';
import './TimeOffRequestDialog.css';

const styles = theme => ({
  radio: {
    '&$checked': {
      color: colors.brand_3
    }
  },
  checked: {}
});

const NINE_AM_TODAY = moment().startOf('day').hours(9).minutes(0).second(0).millisecond(0);
const FIVE_PM_TODAY = moment().startOf('day').hours(17).minutes(0).second(0).millisecond(0);

const formattedType = request => {
  if (request) {
    switch (request.type.toLowerCase()) {
      case 'pto': return 'PTO';
      case 'unpaid': return 'Unpaid';
      case 'sick': return 'Sick';
      default: return '';
    }
  }

  return '';
};

const TimeOffRequestDialog = ({
  isOpen,
  onClose,
  onSubmit,
  makingRequest,
  timeOffRequestAlert,
  currentCenterId,
  request,
  ...props
}) => {
  /**
   * if `request` is null....
   * - creating a new time off request
   * - otherwise we are editing an existing one
   */
  const { classes } = props;
  const [selectedDate, setDate] = useState(request ? moment(request.start) : moment());
  const [requestType, setRequestType] = useState(request && !request.allDay ? 'partialDay' : 'allDay');
  const [timeOffType, setTimeOffType] = useState(request ? formattedType(request) : 'PTO');
  const [timeOffDescription, setDescription] = useState(request ? request.description : '');
  const [startDatetime, setStart] = useState(request ? moment(request.start) : NINE_AM_TODAY);
  const [endDatetime, setEnd] = useState(request ? moment(request.end) : FIVE_PM_TODAY);
  const [totalsForDay, setTotalsForDay] = useState({ total: 0, approved: 0, pending: 0 }); // these keys are what will be sent from the microservice

  const withinTwoWeeks = moment(selectedDate).diff(moment().startOf('day'), 'days') <= 14;
  const isSameDay = moment(endDatetime).isSame(moment(startDatetime), 'date');
  const invalidSelectedDate = moment(selectedDate).diff(moment(), 'days') < 0;
  const invalidTimeframe = moment(endDatetime).isBefore(moment(startDatetime)) || moment(startDatetime).isBefore(moment());
  const buttonDisabled = timeOffType === '' || timeOffDescription === '' || invalidTimeframe || invalidSelectedDate;

  useEffect(() => {
    // reset start and end to 9am-5pm when toggled back to 'All Day' request
    if (requestType === 'allDay' || requestType === 'multiDay') {
      setStart(moment(selectedDate).startOf('day').hours(9).minutes(0).second(0).millisecond(0));
      setEnd(moment(selectedDate).startOf('day').hours(17).minutes(0).second(0).millisecond(0));
    }
  }, [requestType]);

  // when we select a new date fire an api request for info about time off requests for the day
  useEffect(() => {
    (async () => {
      try {
        // only show this information if one date is selected
        if (isSameDay) {
          const formatted = moment(selectedDate).clone().format('YYYY-MM-DD');
          const response = await ktRequest.get(`time-off/requested?date=${formatted}&centerId=${currentCenterId}`, 'timecards');
          const { data } = response;
          setTotalsForDay(data);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [selectedDate]);

  const dismissDialog = () => {
    // reset state before dismissing
    setDescription('');
    setRequestType('allDay');
    setDate(moment());
    setStart(NINE_AM_TODAY);
    setEnd(FIVE_PM_TODAY);

    onClose();
  };

  const generateTimes = () => {
    // generate moment-objects based on the selected date from 6am-9pm
    let times = [];
    let formattedTimes = [];
    let reactSelectTimesArr = [];

    // limit time input options from 6am-9pm
    let start = moment(selectedDate).startOf('day').hours(6).minutes(0).second(0).millisecond(0);
    const end = moment(selectedDate).startOf('day').hours(21).minute(15).second(0).millisecond(0);

    while (start < end) {
      const _start = start;
      times.push(_start.clone());
      formattedTimes.push(_start.clone().format('h:mm A'));
      start = _start.add('m', 15);
    }

    reactSelectTimesArr = formattedTimes.map((time, index) => ({
      label: time,
      value: times[index]
    }));

    return [times, formattedTimes, reactSelectTimesArr];
  };

  const requestOff = () => {
    let end = endDatetime;
    let type = requestType;
    // if we have an invalid end time, default to 5pm today
    if (!end.isValid()) {
      end = moment(startDatetime).clone().hour(17);
    }

    if (type === 'multiDay') {
      type = 'allDay';
    }

    onSubmit(type, timeOffType, startDatetime, end, timeOffDescription);
  };

  // generate new moment objects whenever the selected date changes
  // eslint-disable-next-line
  const [momentTimes, formattedTimes, reactSelectTimesArr] = useMemo(() => generateTimes(), [selectedDate]);

  return (
    <Dialog open={isOpen} onClose={dismissDialog} disableEnforceFocus>
      <Card style={{ overflow: 'auto' }}>
        <CardHeader style={{ backgroundColor: colors.brand_1, color: '#FFFFFF' }} title='Time Off Request' />
        <CardContent>
          { timeOffRequestAlert &&
            <div className='flex' style={{ justifyContent: 'center', marginBottom: 8 }}>
              <Alert type={timeOffRequestAlert.type} title={timeOffRequestAlert.title}>{timeOffRequestAlert.message}</Alert>
            </div>
          }
          <Typography type='subsectionHeader'>How many days would you like to request off?</Typography>
          <div className='time-off-request-container'>
            <div className='time-off-inputs'>
              <RadioGroup
                row
                value={requestType}
                onChange={event => setRequestType(event.target.value)}
              >
                <FormControlLabel value='multiDay' control={<Radio classes={{ root: classes.radio, checked: classes.checked }} />} label='Multiple Days' />
                <FormControlLabel value='allDay' control={<Radio classes={{ root: classes.radio, checked: classes.checked }} />} label='Single Day' />
                <FormControlLabel value='partialDay' control={<Radio classes={{ root: classes.radio, checked: classes.checked }} />} label='Partial Day' />
              </RadioGroup>
              <Select
                label='Time-off Type'
                value={{ label: timeOffType, value: timeOffType }}
                options={[{ label: 'PTO', value: 'PTO' }, { label: 'Unpaid', value: 'Unpaid' }]}
                onChange={option => setTimeOffType(option.value)}
              />
              { requestType === 'partialDay'
                ? <Fragment>
                  <Fade in={requestType === 'partialDay'}>
                    <div style={{ marginTop: 8 }}>
                      <Select
                        label='Start Time'
                        options={reactSelectTimesArr}
                        value={{ label: moment(startDatetime).format('h:mm A'), value: moment(startDatetime) }}
                        onChange={option => setStart(option.value)}
                      />
                    </div>
                  </Fade>
                  <Fade in={requestType === 'partialDay'}>
                    <div style={{ marginTop: 8 }}>
                      <Select
                        label='End Time'
                        options={reactSelectTimesArr}
                        value={{ label: moment(endDatetime).format('h:mm A'), value: moment(endDatetime) }}
                        onChange={option => setEnd(option.value)}
                      />
                    </div>
                  </Fade>
                  { invalidTimeframe && <span className='time-off-error-text'>Invalid timeframe</span> }
                </Fragment>
                : null
              }
              <div>
                <TotalsForDay
                  total={totalsForDay.total}
                  approved={totalsForDay.approved}
                  pending={totalsForDay.pending}
                />
                { withinTwoWeeks && <span className='time-off-error-text'>Notice: Requests made under 2 weeks are not guaranteed.</span> }
              </div>
            </div>
            <div className='time-off-calendar'>
              <DatePickerInput
                disableWeekends
                disableToday={requestType === 'allDay' || requestType === 'multiDay'}
                startDatetime={request ? moment(request.start) : null}
                endDatetime={request ? moment(request.end) : null}
                allowRangeSelect={requestType === 'multiDay'}
                onDateSelect={({ startDate, endDate }) => {
                  setDate(startDate);
                  setStart(startDate);

                  if (endDate.isValid()) {
                    setEnd(endDate);
                  } else {
                    // if end date is not valid (selected the start to a range time off request), default to 5pm of the selected date
                    setEnd(startDate.clone().hours(17).minutes(0).second(0).millisecond(0));
                  }
                }}
              />
              { invalidSelectedDate && <span className='time-off-error-text'>Invalid date. Please select a date after {moment().format('MMMM Do')}.</span> }
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <TextInput
              label='Description'
              value={timeOffDescription}
              onChange={event => setDescription(event.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <Loader loading={makingRequest}>
            <div className='flex-rows' style={{ justifyContent: 'center', marginTop: 8 }}>
              <Button disabled={buttonDisabled} onClick={requestOff}>Submit</Button>
            </div>
          </Loader>
        </CardContent>
      </Card>
    </Dialog>
  );
};

TimeOffRequestDialog.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  makingRequest: PropTypes.bool,
  timeOffRequestAlert: PropTypes.shape({
    type: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string
  }),
  currentCenterId: PropTypes.number,
  request: PropTypes.object
};

TimeOffRequestDialog.defaultProps = {
  request: null // pass this prop when editing a request
};

export default withStyles(styles)(TimeOffRequestDialog);
